import { differenceInCalendarDays } from 'date-fns';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { SUBSIDY_CATEGORY_MAP, SubsidyCategoryType } from '../../../constants/subsidy/subsidyCategory.constant';
import { CITY_EN_MAP, STATE_CODE_MAP } from '../../../constants/subsidy/regionMapping.constant';
import { useGetSubsidiesQuery } from '../../../hooks/api/subsidy/queries/useGetSubsidies.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useScrollDirection } from '../../../hooks/scroll/useScrollDirection';
import { Icon } from '../../../components/common/icon/Icon';
import { SubsidyVerticalListView } from '../../../views/Subsidy/listView/SubsidyVerticalListView';
import { SubsidyCategoryFilter } from '../../../views/Subsidy/listView/components/SubsidyCategoryFilter/SubsidyCategoryFilter';
import { SubsidyRegionFilter } from '../../../views/Subsidy/listView/components/SubsidyRegionFilter/SubsidyRegionFilter';

export const SubsidyListPage = () => {
  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} headerTitle={'지원사업'} hasMyInfoButton />,
    fixedHeaderHeight: 48,
    fixedFooterHeight: 92,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const queryCategory = searchParams.get('category') as SubsidyCategoryType | null;
  const queryState = searchParams.get('state');
  const queryCity = searchParams.get('city');

  // Initialize from URL or default
  const [selectedState, setSelectedState] = useState(queryState ? STATE_CODE_MAP[queryState] ?? '전국' : '전국');
  const [selectedCity, setSelectedCity] = useState(queryCity ? CITY_EN_MAP[queryCity] ?? '전체' : '전체');
  const [selectedCategory, setSelectedCategory] = useState<SubsidyCategoryType>(queryCategory ?? '전체');

  // URL Sync Effects
  useEffect(() => {
    // Sync Category from URL
    if (queryCategory) {
      setSelectedCategory(queryCategory);
    } else {
      setSelectedCategory('전체');
    }

    // Sync State from URL
    const mappedState = queryState ? STATE_CODE_MAP[queryState] : '전국';
    setSelectedState(mappedState ?? '전국');

    // Sync City from URL
    const mappedCity = queryCity ? CITY_EN_MAP[queryCity] : '전체';
    setSelectedCity(mappedCity ?? '전체');
  }, [queryCategory, queryState, queryCity]);

  const handleCategoryChange = (category: SubsidyCategoryType) => {
    setSelectedCategory(category);
    if (category === '전체') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setSelectedCity('전체'); // Reset city

    // Update URL
    const stateCode = STATE_CODE_MAP[state] ?? 'all';
    if (stateCode === 'all') {
      searchParams.delete('state');
      searchParams.delete('city');
    } else {
      searchParams.set('state', stateCode);
      searchParams.delete('city'); // City resets on state change
    }
    setSearchParams(searchParams);
  };

  const handleCityChange = (city: string) => {
    setSelectedCity(city);

    // Update URL
    const citySlug = CITY_EN_MAP[city] ?? 'all';
    if (citySlug === 'all') {
      searchParams.delete('city');
    } else {
      searchParams.set('city', citySlug);
    }
    setSearchParams(searchParams);
  };

  const { data: subsidies, isLoading: isGetSubsidiesQueryLoading } = useGetSubsidiesQuery();

  const { scrollDir, isAtTop } = useScrollDirection();

  const JEONBUK_VARIANTS = ['전북', '전북특별자치도'];
  const JEONNAM_VARIANTS = ['전남', '전라남도'];

  // Debug: Log subsidies from server
  useEffect(() => {
    if (subsidies) {
      console.log('📦 Subsidies from server:', subsidies);
      console.log('📊 Total count:', subsidies.length);
    }
  }, [subsidies]);

  const filteredSubsidies =
    subsidies
      ?.filter((subsidy) => {
        if (!subsidy.isReady) return false;
        if (!subsidy.deadline) return false;

        // Region filtering logic
        const isJeonbukSelected = selectedState === '전북특별자치도';
        const isJeonnamSelected = selectedState === '전라남도';
        const isStateMatch =
          selectedState === '전국' ||
          (isJeonbukSelected && JEONBUK_VARIANTS.includes(subsidy.state ?? '')) ||
          (isJeonnamSelected && JEONNAM_VARIANTS.includes(subsidy.state ?? '')) ||
          subsidy.state === selectedState;

        if (!isStateMatch && !subsidy.isNationwide) return false;

        // City filtering
        if (selectedCity !== '전체') {
          const isCityMatch =
            subsidy.isNationwide ||
            (isStateMatch && (!subsidy.city || subsidy.city === '')) ||
            subsidy.city === selectedCity;

          if (!isCityMatch) return false;
        }

        // Category & tag filtering logic
        if (selectedCategory !== '전체') {
          const targetTags = SUBSIDY_CATEGORY_MAP[selectedCategory as Exclude<SubsidyCategoryType, '전체'>];
          const mappedTargetTagNames = targetTags.map((tag) => tag.name) as string[];

          const hasMatchingTag = subsidy.tags?.some((tag) => mappedTargetTagNames.includes(tag.name));
          if (!hasMatchingTag) return false;
        }

        const today = new Date();
        if (subsidy.estimateDeadline) {
          const estimateDate = new Date(subsidy.estimateDeadline);
          if (differenceInCalendarDays(estimateDate, today) < 0) return false;
        }

        const deadlineDate = new Date(subsidy.deadline);
        return differenceInCalendarDays(deadlineDate, today) >= 0;
      })
      .filter((subsidy) => !['TBD', 'NONE'].includes(subsidy.deadlineType ?? ''))
      .sort((a, b) => {
        // Primary Sort: Order (DESC) - Higher order first
        const orderA = a.order ?? Number.MIN_SAFE_INTEGER;
        const orderB = b.order ?? Number.MIN_SAFE_INTEGER;
        if (orderA !== orderB) {
          return orderB - orderA;
        }

        // Secondary Sort: PostedAt (DESC)
        const dateA = a.postedAt ? new Date(a.postedAt).getTime() : 0;
        const dateB = b.postedAt ? new Date(b.postedAt).getTime() : 0;
        return dateB - dateA;
      }) ?? [];

  return (
    <>
      <Helmet>
        <title>{pageInfo.subsidyListPage.title}</title>
      </Helmet>

      <Container>
        <StickyFilterWrapper isVisible={scrollDir === 'up'} isAtTop={isAtTop}>
          <SubsidyRegionFilter
            selectedState={selectedState}
            selectedCity={selectedCity}
            onStateChange={handleStateChange}
            onCityChange={handleCityChange}
          />

          <SubsidyCategoryFilter selectedCategory={selectedCategory} onCategoryChange={handleCategoryChange} />
        </StickyFilterWrapper>

        {isGetSubsidiesQueryLoading && <AnimateLoading />}

        {!isGetSubsidiesQueryLoading && filteredSubsidies.length > 0 && (
          <ListWrapper>
            {filteredSubsidies.map((subsidy) => (
              <SubsidyVerticalListView key={subsidy.id} subsidy={subsidy} />
            ))}
          </ListWrapper>
        )}

        {!isGetSubsidiesQueryLoading && filteredSubsidies.length === 0 && (
          <EmptyWrapper>
            <Icon name={'EmptyBox2'} width={160} height={160} />
            <EmptyTextSection>
              <EmptyTitle>{'앗! 내 지역 공고가 아직 없어요.'}</EmptyTitle>
            </EmptyTextSection>
          </EmptyWrapper>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 16px;

  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 140px);
`;

const EmptyWrapper = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 12px;
  padding-bottom: 80px;
`;

const EmptyTextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 4px;
`;

const EmptyTitle = styled.h2`
  color: var(--light-scheme-monochrome-offblack, #505363);
  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const StickyFilterWrapper = styled.div<{ isVisible: boolean; isAtTop: boolean }>`
  position: sticky;
  top: 48px;
  z-index: 10;
  background-color: #fff;
  margin: -16px -16px 0 -16px;
  padding: 16px 16px 0 16px;

  transition: transform 0.3s ease-in-out;
  transform: translateY(${(props) => (props.isVisible || props.isAtTop ? '0' : '-110%')});

  box-shadow: ${(props) => (!props.isAtTop ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none')};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;
